import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { SEO, SEOPages } from '../components/common/SEO';
import { EmojiPicker, EmojiAvatarButton } from '../components/common/EmojiPicker';
import { VerificationCodeInput } from '../components/common/VerificationCodeInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MapPin, Users, CreditCard, Calendar, ArrowRight, Loader2, 
  Mail, Lock, User, Sparkles, Wand2, ArrowLeft, RefreshCw, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

const features = [
  { icon: MapPin, title: 'Roteiro Completo', description: 'Organize cada dia da viagem com detalhes' },
  { icon: Users, title: 'Gestão de Grupo', description: 'Convide amigos e gerencie participantes' },
  { icon: CreditCard, title: 'Rateio Justo', description: 'Divida despesas de forma automática' },
  { icon: Calendar, title: 'Acompanhamento', description: 'Visualize o progresso até a viagem' },
];

export default function LandingPage() {
  const [activeTab, setActiveTab] = useState('login');
  const [authMethod, setAuthMethod] = useState('email'); // email, magic
  const [registrationStep, setRegistrationStep] = useState('form'); // form, verification
  const [forgotPasswordStep, setForgotPasswordStep] = useState(null); // null, 'email', 'code', 'newPassword'
  const [loading, setLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    avatar_emoji: '😊',
  });

  const { login, api, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Reset verification step when switching tabs
  useEffect(() => {
    setRegistrationStep('form');
    setForgotPasswordStep(null);
    setVerificationError(null);
    setPendingEmail('');
    setNewPassword('');
    setConfirmPassword('');
  }, [activeTab]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/auth/magic-link/request', { email: formData.email });
      setMagicLinkSent(true);
      toast.success('Link mágico enviado! Verifique seu email.');
    } catch (error) {
      toast.error('Erro ao enviar link');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (activeTab === 'login') {
        await login(formData.email, formData.password);
        toast.success('Bem-vindo de volta! 🎉');
        navigate('/dashboard');
      } else {
        // Step 1: Initialize registration and send verification code
        const response = await api.post('/auth/register/init', {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          avatar_emoji: formData.avatar_emoji
        });
        
        setPendingEmail(formData.email);
        setRegistrationStep('verification');
        toast.success('Código de verificação enviado! 📧');
        
        // Show dev code in console for testing
        if (response.data.dev_code) {
          console.log('DEV CODE:', response.data.dev_code);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Ocorreu um erro. Tente novamente.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (code) => {
    setVerificationLoading(true);
    setVerificationError(null);

    try {
      const response = await api.post('/auth/register/verify', {
        email: pendingEmail,
        code: code
      });
      
      // Store token and redirect
      localStorage.setItem('token', response.data.access_token);
      toast.success('Conta criada com sucesso! ✈️');
      navigate('/dashboard');
      window.location.reload(); // Force reload to update auth state
    } catch (error) {
      const message = error.response?.data?.detail || 'Código inválido. Tente novamente.';
      setVerificationError(message);
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setVerificationError(null);

    try {
      const response = await api.post('/auth/register/resend', {
        email: pendingEmail
      });
      
      toast.success('Novo código enviado! 📧');
      
      // Show dev code in console for testing
      if (response.data.dev_code) {
        console.log('DEV CODE:', response.data.dev_code);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao reenviar código.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setRegistrationStep('form');
    setForgotPasswordStep(null);
    setVerificationError(null);
    setPendingEmail('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // ========== Forgot Password Handlers ==========
  
  const handleForgotPasswordInit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setVerificationError(null);

    try {
      const response = await api.post('/auth/password/forgot', {
        email: formData.email
      });
      
      setPendingEmail(formData.email);
      setForgotPasswordStep('code');
      toast.success('Código enviado para seu email! 📧');
      
      // Show dev code in console for testing
      if (response.data.dev_code) {
        console.log('DEV PASSWORD RESET CODE:', response.data.dev_code);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao solicitar redefinição de senha.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async (code) => {
    setVerificationLoading(true);
    setVerificationError(null);

    try {
      // Just verify the code format is valid and move to password step
      // The actual verification happens when setting the new password
      if (code.length === 6) {
        setPendingEmail(pendingEmail);
        setFormData({ ...formData, resetCode: code });
        setForgotPasswordStep('newPassword');
      } else {
        setVerificationError('Código inválido. Digite os 6 dígitos.');
      }
    } catch (error) {
      setVerificationError('Código inválido. Tente novamente.');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    setVerificationError(null);

    try {
      await api.post('/auth/password/reset', {
        email: pendingEmail,
        code: formData.resetCode,
        new_password: newPassword
      });
      
      toast.success('Senha alterada com sucesso! Faça login com sua nova senha. 🎉');
      
      // Reset to login
      setForgotPasswordStep(null);
      setActiveTab('login');
      setFormData({ ...formData, password: '', resetCode: '' });
      setNewPassword('');
      setConfirmPassword('');
      setPendingEmail('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao redefinir senha.';
      setVerificationError(message);
      
      // If code is invalid, go back to code step
      if (message.includes('Código')) {
        setForgotPasswordStep('code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetCode = async () => {
    setLoading(true);
    setVerificationError(null);

    try {
      const response = await api.post('/auth/password/forgot/resend', {
        email: pendingEmail
      });
      
      toast.success('Novo código enviado! 📧');
      
      // Show dev code in console for testing
      if (response.data.dev_code) {
        console.log('DEV PASSWORD RESET CODE:', response.data.dev_code);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao reenviar código.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen-safe bg-slate-50 dark:bg-slate-950 safe-area flex flex-col">
      <SEO {...SEOPages.landing} />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden flex-1 flex items-center py-8">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1760638260981-2af5fc0c9c57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxncm91cCUyMG9mJTIwZnJpZW5kcyUyMGhpa2luZyUyMG1vdW50YWlufGVufDB8fHx8MTc2ODM0OTg3M3ww&ixlib=rb-4.1.0&q=85"
            alt="Friends hiking"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/80 via-pink-500/70 to-purple-600/80 dark:from-orange-600/90 dark:via-pink-600/80 dark:to-purple-700/90" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Hero Text */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6"
              >
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">Planeje viagens em grupo</span>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Viaje junto,
                <br />
                <span className="text-yellow-300">sem estresse!</span>
              </h1>

              <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-lg">
                Centralize roteiro, pagamentos, caronas e comunicação em um só lugar. 
                Simples, justo e divertido.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl"
                  >
                    <feature.icon className="w-5 h-5 text-yellow-300" />
                    <span className="text-sm font-medium">{feature.title}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: Auth Card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="backdrop-blur-md bg-white/95 dark:bg-slate-900/95 border-0 shadow-2xl rounded-3xl overflow-hidden">
                <CardHeader className="text-center pb-2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.4, type: 'spring' }}
                    className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shadow-lg"
                  >
                    <span className="text-3xl">✈️</span>
                  </motion.div>
                  <CardTitle className="text-2xl font-bold dark:text-white">ViajeJunto</CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    {activeTab === 'login' ? 'Entre na sua conta' : 'Crie sua conta grátis'}
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-6">
                  {/* Auth Method Tabs */}
                  <div className="flex gap-2 mb-4 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                    <button
                      onClick={() => setAuthMethod('email')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        authMethod === 'email' 
                          ? 'bg-white dark:bg-slate-700 shadow-sm text-orange-600 dark:text-orange-400' 
                          : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email
                    </button>
                    <button
                      onClick={() => { setAuthMethod('magic'); setMagicLinkSent(false); }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        authMethod === 'magic' 
                          ? 'bg-white dark:bg-slate-700 shadow-sm text-purple-600 dark:text-purple-400' 
                          : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <Wand2 className="w-4 h-4 inline mr-1" />
                      Magic Link
                    </button>
                  </div>

                  {/* Email Auth */}
                  {authMethod === 'email' && (
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                      <TabsList className="grid w-full grid-cols-2 mb-4 rounded-xl">
                        <TabsTrigger value="login" className="rounded-lg">Entrar</TabsTrigger>
                        <TabsTrigger value="register" className="rounded-lg" disabled={registrationStep === 'verification'}>Cadastrar</TabsTrigger>
                      </TabsList>

                      {/* Registration Verification Step */}
                      {activeTab === 'register' && registrationStep === 'verification' && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6 py-4"
                        >
                          <div className="text-center">
                            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Mail className="w-8 h-8 text-orange-500" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                              Verifique seu email
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              Enviamos um código de 6 dígitos para
                            </p>
                            <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                              {pendingEmail}
                            </p>
                          </div>

                          <VerificationCodeInput
                            onComplete={handleVerifyCode}
                            isLoading={verificationLoading}
                            error={verificationError}
                            autoFocus={true}
                          />

                          <div className="flex flex-col gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleResendCode}
                              disabled={loading}
                              className="w-full h-10 rounded-xl"
                            >
                              {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              Reenviar código
                            </Button>
                            
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={handleBackToForm}
                              className="w-full h-10 rounded-xl text-slate-600 dark:text-slate-400"
                            >
                              <ArrowLeft className="w-4 h-4 mr-2" />
                              Voltar para o cadastro
                            </Button>
                          </div>
                        </motion.div>
                      )}

                      {/* Forgot Password Flow */}
                      {activeTab === 'login' && forgotPasswordStep && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6 py-4"
                        >
                          {/* Step 1: Enter Email */}
                          {forgotPasswordStep === 'email' && (
                            <form onSubmit={handleForgotPasswordInit} className="space-y-4">
                              <div className="text-center">
                                <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <KeyRound className="w-8 h-8 text-orange-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                  Esqueceu sua senha?
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                  Digite seu email para receber um código de recuperação
                                </p>
                              </div>

                              <div>
                                <Label htmlFor="reset-email" className="dark:text-slate-300">Email</Label>
                                <div className="relative mt-1">
                                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                  <Input
                                    id="reset-email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="seu@email.com"
                                    required
                                    className="pl-10 h-12 rounded-xl dark:bg-slate-800 dark:border-slate-700"
                                    data-testid="forgot-password-email"
                                  />
                                </div>
                              </div>

                              <Button
                                type="submit"
                                disabled={loading}
                                className="w-full h-12 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 font-semibold"
                                data-testid="forgot-password-submit"
                              >
                                {loading ? (
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                  <>
                                    Enviar código
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                  </>
                                )}
                              </Button>

                              <Button
                                type="button"
                                variant="ghost"
                                onClick={handleBackToForm}
                                className="w-full h-10 rounded-xl text-slate-600 dark:text-slate-400"
                              >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Voltar para o login
                              </Button>
                            </form>
                          )}

                          {/* Step 2: Enter Code */}
                          {forgotPasswordStep === 'code' && (
                            <>
                              <div className="text-center">
                                <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <Mail className="w-8 h-8 text-orange-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                  Verifique seu email
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                  Enviamos um código de 6 dígitos para
                                </p>
                                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                                  {pendingEmail}
                                </p>
                              </div>

                              <VerificationCodeInput
                                onComplete={handleVerifyResetCode}
                                isLoading={verificationLoading}
                                error={verificationError}
                                autoFocus={true}
                              />

                              <div className="flex flex-col gap-3">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={handleResendResetCode}
                                  disabled={loading}
                                  className="w-full h-10 rounded-xl"
                                >
                                  {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                  )}
                                  Reenviar código
                                </Button>
                                
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={handleBackToForm}
                                  className="w-full h-10 rounded-xl text-slate-600 dark:text-slate-400"
                                >
                                  <ArrowLeft className="w-4 h-4 mr-2" />
                                  Voltar para o login
                                </Button>
                              </div>
                            </>
                          )}

                          {/* Step 3: Enter New Password */}
                          {forgotPasswordStep === 'newPassword' && (
                            <form onSubmit={handleResetPassword} className="space-y-4">
                              <div className="text-center">
                                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <Lock className="w-8 h-8 text-green-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                  Criar nova senha
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                  Digite sua nova senha
                                </p>
                              </div>

                              {verificationError && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                  <p className="text-sm text-red-600 dark:text-red-400 text-center">
                                    {verificationError}
                                  </p>
                                </div>
                              )}

                              <div>
                                <Label htmlFor="new-password" className="dark:text-slate-300">Nova senha</Label>
                                <div className="relative mt-1">
                                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                  <Input
                                    id="new-password"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    className="pl-10 h-12 rounded-xl dark:bg-slate-800 dark:border-slate-700"
                                    data-testid="new-password-input"
                                  />
                                </div>
                              </div>

                              <div>
                                <Label htmlFor="confirm-password" className="dark:text-slate-300">Confirmar senha</Label>
                                <div className="relative mt-1">
                                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                  <Input
                                    id="confirm-password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    className="pl-10 h-12 rounded-xl dark:bg-slate-800 dark:border-slate-700"
                                    data-testid="confirm-password-input"
                                  />
                                </div>
                              </div>

                              <Button
                                type="submit"
                                disabled={loading || newPassword !== confirmPassword || newPassword.length < 6}
                                className="w-full h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 font-semibold"
                                data-testid="reset-password-submit"
                              >
                                {loading ? (
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                  <>
                                    Salvar nova senha
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                  </>
                                )}
                              </Button>

                              <Button
                                type="button"
                                variant="ghost"
                                onClick={handleBackToForm}
                                className="w-full h-10 rounded-xl text-slate-600 dark:text-slate-400"
                              >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Voltar para o login
                              </Button>
                            </form>
                          )}
                        </motion.div>
                      )}

                      {/* Regular Form - Show when not in verification step or forgot password */}
                      {!(activeTab === 'register' && registrationStep === 'verification') && !(activeTab === 'login' && forgotPasswordStep) && (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Register: Avatar + Name */}
                        <AnimatePresence>
                          {activeTab === 'register' && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="space-y-4"
                            >
                              {/* Avatar Selection */}
                              <div className="flex items-center justify-center gap-4">
                                <div className="relative">
                                  <EmojiAvatarButton
                                    emoji={formData.avatar_emoji}
                                    size="xl"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    className="absolute -bottom-1 -right-1 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs shadow-lg hover:bg-orange-600 transition-colors"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                <div className="text-left">
                                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Seu avatar</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">Clique para escolher</p>
                                </div>
                              </div>

                              {/* Emoji Picker Popup */}
                              <AnimatePresence>
                                {showEmojiPicker && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="absolute z-50 left-1/2 -translate-x-1/2"
                                  >
                                    <EmojiPicker
                                      selectedEmoji={formData.avatar_emoji}
                                      onSelect={(emoji) => {
                                        setFormData({ ...formData, avatar_emoji: emoji });
                                        setShowEmojiPicker(false);
                                      }}
                                      onClose={() => setShowEmojiPicker(false)}
                                    />
                                  </motion.div>
                                )}
                              </AnimatePresence>

                              <div>
                                <Label htmlFor="name" className="dark:text-slate-300">Como podemos te chamar?</Label>
                                <div className="relative mt-1">
                                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                  <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Seu nome"
                                    required={activeTab === 'register'}
                                    className="pl-10 h-12 rounded-xl dark:bg-slate-800 dark:border-slate-700"
                                  />
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div>
                          <Label htmlFor="email" className="dark:text-slate-300">Email</Label>
                          <div className="relative mt-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              id="email"
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="seu@email.com"
                              required
                              className="pl-10 h-12 rounded-xl dark:bg-slate-800 dark:border-slate-700"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="password" className="dark:text-slate-300">Senha</Label>
                          <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              id="password"
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder="••••••••"
                              required
                              className="pl-10 h-12 rounded-xl dark:bg-slate-800 dark:border-slate-700"
                            />
                          </div>
                        </div>

                        {/* Forgot Password Link - Only show on login tab */}
                        {activeTab === 'login' && !forgotPasswordStep && (
                          <div className="text-right">
                            <button
                              type="button"
                              onClick={() => setForgotPasswordStep('email')}
                              className="text-sm text-orange-600 dark:text-orange-400 hover:underline"
                              data-testid="forgot-password-link"
                            >
                              Esqueci minha senha
                            </button>
                          </div>
                        )}

                        <Button
                          type="submit"
                          disabled={loading}
                          className="w-full h-12 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 font-semibold text-base"
                        >
                          {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              {activeTab === 'login' ? 'Entrar' : 'Criar conta'}
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </>
                          )}
                        </Button>
                      </form>
                      )}
                    </Tabs>
                  )}

                  {/* Magic Link Auth */}
                  {authMethod === 'magic' && (
                    <div className="space-y-4">
                      {!magicLinkSent ? (
                        <form onSubmit={handleMagicLink}>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 text-center">
                            Receba um link mágico no seu email. Sem senha!
                          </p>
                          <div className="mb-4">
                            <Label htmlFor="magic-email" className="dark:text-slate-300">Email</Label>
                            <div className="relative mt-1">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                id="magic-email"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="seu@email.com"
                                required
                                className="pl-10 h-12 rounded-xl dark:bg-slate-800 dark:border-slate-700"
                              />
                            </div>
                          </div>
                          <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 font-semibold"
                          >
                            {loading ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <>
                                <Wand2 className="w-4 h-4 mr-2" />
                                Enviar link mágico
                              </>
                            )}
                          </Button>
                        </form>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="text-center py-6"
                        >
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <span className="text-3xl">✨</span>
                          </div>
                          <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">
                            Link enviado!
                          </h3>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Verifique seu email e clique no link mágico para entrar.
                          </p>
                          <Button
                            variant="ghost"
                            className="mt-4"
                            onClick={() => setMagicLinkSent(false)}
                          >
                            Tentar outro email
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
